import React, { useMemo } from "react";
import {
  Alert,
  Box,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
} from "@mui/material";
import HPInput from "@components/common/Input";
import HPDropdown from "@components/common/Dropdown";
import HPButton from "@components/common/Button";
import HiddenInDatasetBadge from "@components/common/HiddenInDatasetBadge";
import { t } from "@utils/i18n";

const SUM_SELECTED_METHODOLOGY_EXPLANATION =
  "SUM_SELECTED adds every qualifying mention contributed by the selected source lines. In contexts where a respondent can contribute to more than one selected line, the rollup may exceed 100%.";

// Same rules as focus-level rollup name validation in RollupBuilder.tsx
const ALLOWED_CHARS = /^[a-zA-Z0-9 _\-]*$/;
const MAX_NAME_LENGTH = 100;

export interface BarOption {
  sourceLabel: string;
  displayLabel: string;
  originalLabel?: string;
}

interface CategoryRollupBuilderProps {
  barOptions: BarOption[];
  existingNames: Set<string>;
  claimedSourceLabels: Set<string>;
  claimedSourceLabelToRollupName?: Map<string, string>;
  ineligibleSourceLabels?: Map<string, string>;
  /** Sources hidden in the dataset — omitted from new picks entirely, kept
   *  (and tagged) only if already part of the rollup being edited. */
  hiddenSourceLabels?: Set<string>;
  name: string;
  selectedSourceLabels: string[];
  onNameChange: (name: string) => void;
  onSelectionChange: (labels: string[]) => void;
  onCancel: () => void;
  onCreate: () => void;
  isEditing?: boolean;
  itemLabel?: string;
  nameLabel?: string;
}

const CategoryRollupBuilder: React.FC<CategoryRollupBuilderProps> = ({
  barOptions,
  existingNames,
  claimedSourceLabels,
  claimedSourceLabelToRollupName,
  ineligibleSourceLabels,
  hiddenSourceLabels,
  name,
  selectedSourceLabels,
  onNameChange,
  onSelectionChange,
  onCancel,
  onCreate,
  isEditing = false,
  itemLabel = "bars",
  nameLabel = "Rollup Name*",
}) => {
  const capitalizedItemLabel =
    itemLabel.charAt(0).toUpperCase() + itemLabel.slice(1);
  const hiddenReasonText = t("common.hiddenInDataset");
  const dropdownOptions = useMemo(
    () =>
      barOptions
        // A hidden source is only offered if it's already part of this
        // rollup's saved selection (so its chip keeps rendering) — never as
        // a new pick.
        .filter(
          (bar) =>
            !hiddenSourceLabels?.has(bar.sourceLabel) ||
            selectedSourceLabels.includes(bar.sourceLabel),
        )
        .map((bar) => {
          const claimingRollupName = claimedSourceLabelToRollupName?.get(
            bar.sourceLabel,
          );
          const ineligibleReason = ineligibleSourceLabels?.get(bar.sourceLabel);
          const subtitle = hiddenSourceLabels?.has(bar.sourceLabel)
            ? hiddenReasonText
            : ineligibleReason
              ? ineligibleReason
              : claimingRollupName
                ? `Included in ${claimingRollupName}`
                : bar.originalLabel !== undefined &&
                    bar.originalLabel !== bar.displayLabel
                  ? bar.originalLabel
                  : undefined;
          return {
            value: bar.sourceLabel,
            label: bar.displayLabel,
            subtitle,
          };
        }),
    [
      barOptions,
      claimedSourceLabelToRollupName,
      ineligibleSourceLabels,
      hiddenSourceLabels,
      selectedSourceLabels,
      hiddenReasonText,
    ],
  );

  const hasHiddenSelectedMember = selectedSourceLabels.some((label) =>
    hiddenSourceLabels?.has(label),
  );

  const nameError = useMemo((): string | null => {
    if (!name) return null;
    const trimmed = name.trim();
    if (!trimmed) return "Name is required";
    if (trimmed.length > MAX_NAME_LENGTH)
      return `Name must be ${MAX_NAME_LENGTH} characters or fewer`;
    if (!ALLOWED_CHARS.test(trimmed))
      return "Use letters, numbers, spaces, hyphens, or underscores only";
    if (existingNames.has(trimmed.toLowerCase()))
      return "A category with this name already exists. Use a unique rollup name.";
    return null;
  }, [name, existingNames]);

  const crossRollupError = useMemo((): string | null => {
    const hasClaimedBar = selectedSourceLabels.some((l) =>
      claimedSourceLabels.has(l),
    );
    return hasClaimedBar
      ? `Some selected ${itemLabel} are already used in another rollup.`
      : null;
  }, [selectedSourceLabels, claimedSourceLabels, itemLabel]);

  const ineligibleError = useMemo((): string | null => {
    if (!ineligibleSourceLabels) return null;
    const selectedIneligible = selectedSourceLabels.find((l) =>
      ineligibleSourceLabels.has(l),
    );
    return selectedIneligible
      ? (ineligibleSourceLabels.get(selectedIneligible) ?? null)
      : null;
  }, [selectedSourceLabels, ineligibleSourceLabels]);

  const selectionError =
    ineligibleError ??
    crossRollupError ??
    (selectedSourceLabels.length < 2 ? `Select at least 2 ${itemLabel}` : null);

  const isValid =
    name.trim().length > 0 &&
    !nameError &&
    selectedSourceLabels.length >= 2 &&
    !crossRollupError &&
    !ineligibleError;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <HPInput
        label={nameLabel}
        value={name}
        onChange={(e) => onNameChange(e.target.value)}
        placeholder="Enter a name for this rollup"
        error={!!nameError}
        helperText={nameError ?? ""}
      />

      <HPDropdown
        label={`Source ${capitalizedItemLabel}* (select at least 2)`}
        value={selectedSourceLabels}
        onChange={onSelectionChange}
        options={dropdownOptions}
        multiSelect
        countLabel={itemLabel}
        placeholder={`Choose ${itemLabel}`}
        error={!!selectionError}
        helperText={selectionError ?? ""}
        getOptionDisabled={(opt) =>
          claimedSourceLabels.has(opt.value) ||
          (ineligibleSourceLabels?.has(opt.value) ?? false) ||
          (hiddenSourceLabels?.has(opt.value) ?? false)
        }
        noOptionsText={`No matching ${itemLabel}`}
      />
      {hasHiddenSelectedMember && <HiddenInDatasetBadge />}

      <Alert severity="info" sx={{ fontSize: "0.8125rem" }}>
        {SUM_SELECTED_METHODOLOGY_EXPLANATION}
      </Alert>

      <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
        <HPButton variant="outlined" size="small" onClick={onCancel}>
          Cancel
        </HPButton>
        <HPButton
          variant="contained"
          size="small"
          onClick={onCreate}
          disabled={!isValid}
        >
          {isEditing ? "Save" : "Create"}
        </HPButton>
      </Box>
    </Box>
  );
};

export default CategoryRollupBuilder;
